import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useOverdueCustomer } from 'src/contexts/OverdueCustomerContext';
import useGlotaT from 'src/hooks/useGlotaTranslation';
import { Button, Icon } from '@polichat/flamboyant';
import {
  CloseLineLight,
  InfoLine,
  MailLineLight,
  OpenLineLight,
} from '@poli-digital/react';
import { toast } from 'react-toastify';

export const OverdueCustomerContent = () => {
  const { overdueCustomers, hasDebt, setShowOverdueCustomerModal } =
    useOverdueCustomer();
  const superRole = useSelector(
    (state: any) => state.chat?.config?.user?.superRole
  );
  const brand = useSelector((state: any) => state.whitelabel?.config);

  const overdueCustomerModalTitle = useGlotaT('spa.overdueCustomerModal.title');
  const overdueCustomerModalSubtitle = useGlotaT(
    'spa.overdueCustomerModal.subtitle'
  );
  const overdueCustomerModalHasDebtTitle = useGlotaT(
    'spa.overdueCustomerModal.hasDebt.title'
  );
  const overdueCustomerModalHasDebtSubtitle = useGlotaT(
    'spa.overdueCustomerModal.hasDebt.subtitle'
  );
  const overdueCustomerModalTableDueDate = useGlotaT(
    'spa.overdueCustomerModal.table.dueDate'
  );
  const overdueCustomerModalTableValue = useGlotaT(
    'spa.overdueCustomerModal.table.value'
  );
  const overdueCustomerModalTableCorrectedValue = useGlotaT(
    'spa.overdueCustomerModal.table.correctedValue'
  );
  const overdueCustomerModalTableAccessBoleto = useGlotaT(
    'spa.overdueCustomerModal.table.accessBoleto'
  );
  const overdueCustomerModalInfoLineTitle = useGlotaT(
    'spa.overdueCustomerModal.infoLine.title'
  );
  const overdueCustomerModalInfoLineSubtitle = useGlotaT(
    'spa.overdueCustomerModal.infoLine.subtitle'
  );
  const overdueCustomerModalContactHeader = useGlotaT(
    'spa.overdueCustomerModal.contactHeader'
  );
  const overdueCustomerModalContactButtonEmail = useGlotaT(
    'spa.overdueCustomerModal.contactButton.email'
  );
  const overdueCustomerModalContactButtonWhatsapp = useGlotaT(
    'spa.overdueCustomerModal.contactButton.whatsapp'
  );
  const overdueCustomerModalContactButtonWlWhatsapp = useGlotaT(
    'spa.overdueCustomerModal.contactButton.wl.Whatsapp'
  );
  const overdueCustomerModalContactButtonEmailCopied = useGlotaT(
    'spa.overdueCustomerModal.contactButton.emailCopied'
  );

  const formatLink = (link: string | undefined): string => {
    if (!link) return '';
    // Caso já tenha o http ou https, retorna como está
    if (link.startsWith('http://') || link.startsWith('https://')) {
      return link;
    }
    // Se não tem protocolo, adiciona https://
    return `https://${link}`;
  };

  return (
    <>
      <div className={`over-due-header ${hasDebt ? 'has-debt' : 'suspended'}`}>
        <span>
          {hasDebt
            ? overdueCustomerModalHasDebtTitle
            : overdueCustomerModalTitle}
        </span>
        {hasDebt && (
          <Button
            className="p-2"
            onClick={() => {
              setShowOverdueCustomerModal(false);
            }}
          >
            <CloseLineLight width="12" height="12" color="var(--textDefault)" />
          </Button>
        )}
      </div>
      <span className="over-due-subtitle">
        {hasDebt
          ? overdueCustomerModalHasDebtSubtitle
          : overdueCustomerModalSubtitle}
      </span>
      {['adm', 'manager'].includes(superRole) &&
        (overdueCustomers?.invoices?.length ?? 0) > 0 && (
          <div className="over-due-table-container">
            <table>
              <thead>
                <tr>
                  <th>{overdueCustomerModalTableDueDate}</th>
                  <th>{overdueCustomerModalTableValue}</th>
                  <th>{overdueCustomerModalTableCorrectedValue}</th>
                  <th>{overdueCustomerModalTableAccessBoleto}</th>
                </tr>
              </thead>
              <tbody>
                {overdueCustomers?.invoices?.map((invoice) => (
                  <tr>
                    <td>
                      {invoice?.due_date
                        ? format(new Date(invoice?.due_date), 'dd/MM/yyyy')
                        : ''}
                    </td>
                    <td>
                      {invoice?.value.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </td>
                    <td>
                      {invoice?.corrected_value.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </td>
                    <td>
                      <Button
                        onClick={() => {
                          const link = formatLink(invoice?.link_invoice);
                          if (link) {
                            window.open(link, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        <OpenLineLight width="24" height="24" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {!hasDebt && (
        <div className="over-due-info-line">
          <div className="over-due-info-line-icon">
            <InfoLine
              color={'var(--secondary)'}
              width="24"
              height="24"
              strokeWidth={4}
            />
          </div>
          <div className="over-due-info-line-content">
            <span className="over-due-info-line-header">
              {overdueCustomerModalInfoLineTitle}
            </span>
            <span className="over-due-info-line-subtitle">
              {overdueCustomerModalInfoLineSubtitle}
            </span>
          </div>
        </div>
      )}
      {['Poli', 'ChatsHub'].includes(brand?.app_title) ? (
        <>
          <span className="over-due-contact-header">
            {overdueCustomerModalContactHeader}
          </span>
          <Button
            className="over-due-contact-button email-button"
            outline
            color="primary"
            onClick={() => {
              navigator.clipboard.writeText(
                brand?.app_title === 'ChatsHub'
                  ? 'faturamento@chatshub.com.br'
                  : 'faturamento@poli.digital'
              );
              toast.success(overdueCustomerModalContactButtonEmailCopied);
            }}
          >
            <MailLineLight color="var(--primary)" width="18" height="18" />
            <span>{overdueCustomerModalContactButtonEmail}</span>
          </Button>
          <Button
            className="over-due-contact-button whatsapp-button"
            onClick={() => {
              if (brand?.app_title === 'ChatsHub') {
                window.open(
                  'https://api.whatsapp.com/send?phone=556291732615',
                  '_blank',
                  'noopener,noreferrer'
                );
              } else {
                window.open(
                  'https://api.whatsapp.com/send?phone=556299800123',
                  '_blank',
                  'noopener,noreferrer'
                );
              }
            }}
          >
            <Icon icon="poli-icon pi-whatsapp-line" size={24} />
            <span>{overdueCustomerModalContactButtonWhatsapp}</span>
          </Button>
        </>
      ) : (
        <>
          <span className="over-due-contact-button wl-subtitle">
            {overdueCustomerModalContactButtonWlWhatsapp}
          </span>
        </>
      )}
    </>
  );
};
